import React from 'react';
import { useNavigate } from 'react-router-dom';
import MaintenanceActionsDropdown, { type MaintenanceAction } from '../../components/admin/MaintenanceActionsDropdown';
import type { InstallationListRow, InstallationStatus } from '../../data/installations';

type Props = {
  job: InstallationListRow;
  isAdmin: boolean;
  busy?: boolean;
  onTransition: (status: 'in_progress' | 'completed' | 'lost' | 'cancelled') => void | Promise<void>;
  onArchive: () => void | Promise<void>;
  onRestore: () => void | Promise<void>;
};

function isTerminal(status: InstallationStatus) {
  return status === 'completed' || status === 'lost' || status === 'cancelled';
}

export default function InstallationActionsDropdown({
  job,
  isAdmin,
  busy = false,
  onTransition,
  onArchive,
  onRestore,
}: Props) {
  const navigate = useNavigate();
  const open = React.useCallback((suffix = '') => navigate(`/installations/${job.id}${suffix}`), [job.id, navigate]);
  const actions = React.useMemo<MaintenanceAction[]>(() => {
    if (job.deletedAt) {
      return [
        { key: 'open', label: 'View archived job', onSelect: () => open() },
        ...(isAdmin ? [{ key: 'restore', label: 'Restore job', disabled: busy, onSelect: onRestore }] : []),
      ];
    }
    const next: MaintenanceAction[] = [
      { key: 'open', label: 'Open job', onSelect: () => open() },
      ...(!['lost', 'cancelled'].includes(job.status) ? [{ key: 'edit', label: 'Edit job details', onSelect: () => open('?tab=basic&edit=1') }] : []),
      { key: 'schedule', label: 'Open schedule', onSelect: () => open('?tab=schedule') },
    ];
    if (job.clientId) next.push({ key: 'client', label: 'Open client', onSelect: () => navigate(`/clients/${job.clientId}/installations`) });
    if (job.status === 'confirmed') next.push({ key: 'progress', label: 'Mark In Progress', disabled: busy, onSelect: () => onTransition('in_progress') });
    if (job.status === 'in_progress') next.push({ key: 'complete', label: 'Mark Complete', disabled: busy, onSelect: () => onTransition('completed') });
    if (job.status === 'completed') next.push({ key: 'completion-review', label: 'Completion review', onSelect: () => open('?tab=completion') });
    if (job.quoteType !== 'service_agreement' && ['confirmed', 'in_progress'].includes(job.status)) {
      next.push({ key: 'change-order', label: 'Create Change Order', onSelect: () => open('?tab=basic&changeOrder=1') });
    }
    if (!isTerminal(job.status)) {
      if (!['confirmed', 'in_progress'].includes(job.status)) next.push({ key: 'lost', label: 'Mark Lost', disabled: busy, onSelect: () => onTransition('lost') });
      next.push({ key: 'cancel', label: 'Cancel job', disabled: busy, onSelect: () => onTransition('cancelled') });
    }
    if (isAdmin && isTerminal(job.status)) next.push({ key: 'archive', label: 'Delete job', tone: 'danger', disabled: busy, onSelect: onArchive });
    return next;
  }, [busy, isAdmin, job.clientId, job.deletedAt, job.status, job.quoteType, navigate, onArchive, onRestore, onTransition, open]);

  return (
    <MaintenanceActionsDropdown
      actions={actions}
      label="Actions"
      menuLabel={`Actions for ${job.code}`}
      stopPropagation
    />
  );
}
